import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Clock,
  ShoppingCart,
  Search,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  MousePointerClick,
  Store,
} from 'lucide-react';
import apiService from '../services/api';
import {
  CLOUDINARY_ADMIN_WIDTH,
  cloudinaryDeliveryUrl,
} from '../utils/cloudinaryDelivery';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function ClientComportementSite({
  clientId,
  commercantId: commercantIdProp,
  allowCommercantPicker = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periode, setPeriode] = useState('90j');
  const [expandedSession, setExpandedSession] = useState(null);
  const [selectedCommercantId, setSelectedCommercantId] = useState(commercantIdProp || '');
  const [allCommercants, setAllCommercants] = useState([]);
  const [commercantsLoading, setCommercantsLoading] = useState(false);

  const effectiveCommercantId = commercantIdProp || selectedCommercantId || null;

  useEffect(() => {
    if (!allowCommercantPicker || commercantIdProp) return;

    let cancelled = false;
    (async () => {
      try {
        setCommercantsLoading(true);
        const res = await apiService.getCommercants({ actif: true, limit: 500 });
        const list = Array.isArray(res) ? res : (res?.commercants || res?.data || []);
        if (!cancelled) {
          setAllCommercants(
            list
              .filter((c) => c.actif !== false)
              .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'))
          );
        }
      } catch (err) {
        console.error('[ClientComportementSite] chargement commercants', err);
      } finally {
        if (!cancelled) setCommercantsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allowCommercantPicker, commercantIdProp]);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      setError('');
      const res = await apiService.getClientComportement(clientId, {
        commercantId: effectiveCommercantId,
        period: periode,
      });
      setData(res);
      if (res.mode === 'multi' && res.parCommercant?.length && !effectiveCommercantId) {
        setSelectedCommercantId('');
      }
    } catch (err) {
      console.error(err);
      setError('Impossible de charger le comportement sur le site.');
    } finally {
      setLoading(false);
    }
  }, [clientId, effectiveCommercantId, periode]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-red-800 text-sm">
        {error}
        <button type="button" onClick={load} className="ml-2 underline font-medium">
          Réessayer
        </button>
      </div>
    );
  }

  if (data?.mode === 'multi') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-600">
            Activité site sur toutes les boutiques — choisissez une boutique pour le détail complet.
          </p>
          <PeriodPicker periode={periode} onChange={setPeriode} />
        </div>

        {allowCommercantPicker && (
          <CommercantPicker
            commercants={allCommercants}
            loading={commercantsLoading}
            value={selectedCommercantId}
            onChange={setSelectedCommercantId}
          />
        )}

        {data.parCommercant?.length === 0 ? (
          allowCommercantPicker ? (
            <div className="rounded-lg bg-violet-50 border border-violet-100 p-6 text-center text-sm text-violet-900">
              <Globe className="h-10 w-10 text-violet-300 mx-auto mb-3" />
              <p className="font-medium">Aucune activité site enregistrée sur la période.</p>
              <p className="text-violet-700 mt-1">
                Sélectionnez une boutique ci-dessus pour consulter le détail (même sans tracking).
              </p>
            </div>
          ) : (
            <EmptyState />
          )
        ) : (
          <div className="grid gap-3">
            {data.parCommercant.map((c) => (
              <button
                key={c.commercantId}
                type="button"
                onClick={() => setSelectedCommercantId(c.commercantId)}
                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-violet-300 hover:bg-violet-50/50 text-left transition"
              >
                <div className="flex items-center gap-3">
                  {c.logo ? (
                    <img
                      src={cloudinaryDeliveryUrl(c.logo, { width: CLOUDINARY_ADMIN_WIDTH.logo }) || c.logo}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <Store className="h-5 w-5 text-violet-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{c.nom}</p>
                    <p className="text-xs text-gray-500">
                      {c.passages} passage{c.passages > 1 ? 's' : ''} · {c.pagesVues} pages vues
                    </p>
                  </div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <p>Dernière visite</p>
                  <p className="font-medium text-gray-700">{fmtDate(c.derniereVisite)}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {selectedCommercantId && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setSelectedCommercantId('')}
              className="text-sm text-violet-600 hover:underline mb-4"
            >
              ← Retour à la liste des boutiques
            </button>
            <ClientComportementSite
              clientId={clientId}
              commercantId={selectedCommercantId}
              allowCommercantPicker={false}
            />
          </div>
        )}
      </div>
    );
  }

  const { resume, commercant } = data || {};

  return (
    <div className="space-y-6">
      {!commercantIdProp && selectedCommercantId && (
        <button
          type="button"
          onClick={() => setSelectedCommercantId('')}
          className="text-sm text-violet-600 hover:underline"
        >
          ← Retour à la vue multi-boutiques
        </button>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {commercant && (
            <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              <Globe className="h-4 w-4 text-violet-600" />
              Boutique : {commercant.nom}
            </p>
          )}
          {data?.periode && (
            <p className="text-xs text-gray-500 mt-0.5">
              Activité identifiée
              {data.periode ? ` · ${data.periode.debutLocal} → ${data.periode.finLocal}` : ''}
            </p>
          )}
        </div>
        <PeriodPicker periode={periode} onChange={setPeriode} />
      </div>

      {allowCommercantPicker && (
        <CommercantPicker
          commercants={allCommercants}
          loading={commercantsLoading}
          value={selectedCommercantId}
          onChange={setSelectedCommercantId}
        />
      )}

      {!data?.trackingActif ? (
        <EmptyState />
      ) : (
        <>
          {data.dernierAbandon && (
            <div
              className={`rounded-lg px-4 py-3 text-sm border ${
                data.dernierAbandon.type === 'converti'
                  ? 'bg-emerald-50 border-emerald-100 text-emerald-900'
                  : 'bg-amber-50 border-amber-100 text-amber-900'
              }`}
            >
              <AlertCircle className="h-4 w-4 inline mr-1.5 align-text-bottom" />
              {data.dernierAbandon.label}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Passages boutique" value={resume?.passages ?? 0} icon={Globe} />
            <StatCard label="Pages vues" value={resume?.pagesVues ?? 0} icon={Eye} />
            <StatCard
              label="Commandes site"
              value={resume?.commandesSite ?? 0}
              icon={ShoppingCart}
            />
            <StatCard
              label="Conversion"
              value={`${resume?.tauxConversionPassageCommande ?? 0}%`}
              sub="passages → commande"
              icon={MousePointerClick}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Activité</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Première visite</dt>
                  <dd className="font-medium">{fmtDate(resume?.premiereVisite)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Dernière visite</dt>
                  <dd className="font-medium">{fmtDate(resume?.derniereVisite)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Pages / passage</dt>
                  <dd className="font-medium">{resume?.pagesParPassage ?? 0}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Ajouts panier</dt>
                  <dd className="font-medium">{resume?.ajoutsPanier ?? 0}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Checkouts démarrés</dt>
                  <dd className="font-medium">{resume?.checkouts ?? 0}</dd>
                </div>
                {data.fidelite && (
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <dt className="text-gray-500">Fidélité boutique</dt>
                    <dd className="font-medium">
                      {data.fidelite.points} pts · {data.fidelite.nbCommandes} cmd
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Heures d&apos;activité
              </h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(data.heuresActivite || [])
                  .filter((h) => h.actions > 0)
                  .sort((a, b) => b.actions - a.actions)
                  .slice(0, 8)
                  .map((h) => (
                    <div key={h.heure} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {h.heure}h – {h.heure + 1}h
                      </span>
                      <span className="font-medium">{h.actions} actions</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {(data.produitsConsultes || []).length > 0 && (
            <Section title="Produits consultés" icon={Eye}>
              <div className="space-y-2">
                {data.produitsConsultes.map((p) => (
                  <div key={p.produitId} className="flex justify-between text-sm gap-2">
                    <span className="text-gray-800 truncate">{p.nom}</span>
                    <span className="shrink-0 text-gray-500">
                      {p.vues} vues · {fmtDate(p.derniereVue)}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {(data.recherches || []).length > 0 && (
            <Section title="Recherches" icon={Search}>
              <div className="space-y-2">
                {data.recherches.map((r) => (
                  <div key={r.query} className="flex justify-between text-sm">
                    <span>« {r.query} »</span>
                    <span className="text-gray-500">{r.nb}×</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {(data.commandesSite || []).length > 0 && (
            <Section title="Commandes passées sur le site" icon={ShoppingCart}>
              <div className="space-y-2">
                {data.commandesSite.map((c) => (
                  <div
                    key={c.id}
                    className="flex justify-between items-center text-sm gap-2 p-2 rounded bg-white border border-gray-100"
                  >
                    <div>
                      <p className="font-medium">{c.numero || c.id.slice(-8)}</p>
                      <p className="text-xs text-gray-500">{fmtDate(c.date)} · {c.modeRetrait}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{Number(c.total || 0).toFixed(2)} €</p>
                      <Link
                        to={`/commandes?highlight=${c.id}`}
                        className="text-xs text-violet-600 hover:underline"
                      >
                        Voir commande
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Historique des passages (sessions)" icon={MousePointerClick}>
            <div className="space-y-2">
              {(data.sessions || []).map((s) => {
                const open = expandedSession === s.sessionId;
                return (
                  <div key={s.sessionId} className="rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedSession(open ? null : s.sessionId)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white hover:bg-gray-50 text-left"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {fmtDate(s.debut)}
                          {s.converti && (
                            <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                              commande
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {s.dureeMinutes} min · {s.nbActions} actions
                        </p>
                      </div>
                      {open ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    {open && (
                      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 space-y-1">
                        {(s.actions || []).map((a, i) => (
                          <div key={i} className="flex justify-between text-xs gap-2">
                            <span className="text-gray-700">{a.label}</span>
                            <span className="text-gray-400 shrink-0">{fmtTime(a.at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {(data.timeline || []).length > 0 && (
            <Section title="Fil d'activité récent" icon={Clock}>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {data.timeline.map((ev, i) => (
                  <div key={i} className="flex justify-between text-xs gap-2 py-1 border-b border-gray-100 last:border-0">
                    <span className="text-gray-800">{ev.label}</span>
                    <span className="text-gray-400 shrink-0">{fmtDate(ev.at)}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function CommercantPicker({ commercants, loading, value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <label htmlFor="comportement-commercant-filter" className="text-sm font-medium text-gray-700 shrink-0">
        Filtrer par boutique
      </label>
      <select
        id="comportement-commercant-filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="min-w-[220px] flex-1 max-w-md rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
      >
        <option value="">
          {loading ? 'Chargement des boutiques…' : 'Toutes les boutiques (vue agrégée)'}
        </option>
        {commercants.map((c) => (
          <option key={c.id} value={c.id}>
            {c.nom}
          </option>
        ))}
      </select>
    </div>
  );
}

function PeriodPicker({ periode, onChange }) {
  return (
    <div className="flex gap-1">
      {['7j', '30j', '90j', '1a'].map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`px-2.5 py-1 rounded text-xs font-medium ${
            periode === p ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="rounded-lg bg-white border border-gray-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4">
      <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-violet-600" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg bg-violet-50 border border-violet-100 px-4 py-8 text-center text-sm text-violet-900">
      <Globe className="h-8 w-8 mx-auto mb-2 text-violet-400" />
      Aucune activité identifiée pour ce client sur la période.
      <p className="text-xs mt-2 text-violet-700">
        Activité identifiée uniquement (sessions anonymes non rattachées). Les données
        apparaissent lorsque le client visite la boutique en ligne (connecté).
      </p>
    </div>
  );
}
